import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AdsSettingsService } from '../../ads-settings-service.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-ads-file-upload',
  templateUrl: './ads-file-upload.component.html',
  styleUrl: './ads-file-upload.component.scss',
  standalone: false
})
export class AdsFileUploadComponent {
  @Input() selectedShops: any[] = [];
  @Input() adId: number = 0;

  serverMedia: { name: string, url: string }[] = [];

  imageFiles: File[] = [];
  imagePreviews: string[] = [];
  videoFiles: File[] = [];
  videoPreviews: string[] = [];

  isImageDragOver = false;
  isVideoDragOver = false;

  uploadProgress = 0;
  isUploading = false;
  currentUploadingFile = '';

  adForm: FormGroup;

  get minDate(): Date {
    const control = this.adForm.get('startDate');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If the field hasn't been edited yet and has an existing past date, 
    // allow the existing past date to be valid.
    if (control && !control.dirty && control.value) {
      const val = new Date(control.value);
      val.setHours(0, 0, 0, 0);
      if (val < today) {
        return val;
      }
    }

    // Once edited, enforce the minimum date to be today
    return today;
  }

  get minEndDate(): any {
    const start = this.adForm.get('startDate')?.value;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return start ? start : today;
  }

  constructor(
    private adsSettingService: AdsSettingsService,
    private fb: FormBuilder,
    private snackBarService: SnackBarService,
    private router: Router,
    private dialog: MatDialog
  ) {
    this.adForm = this.fb.group({
      adTitle: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: [''],
      mediaType: ['image', Validators.required],
      imageDuration: [10, [Validators.required, Validators.min(1)]],
      muteVideo: [true]
    });
  }

  update() {
    if (!this.adForm.valid) {
      this.adForm.markAllAsTouched();
      this.showError('Please fill all required fields correctly.');
      return;
    }

    if (!this.selectedShops?.length) {
      this.showError('Please select at least one shop.');
      return;
    }

    const mediaType = this.adForm.value.mediaType === 'image' ? 1 : 2;
    const filesToUpload = mediaType === 1 ? this.imageFiles : this.videoFiles;
    const hasExistingMedia = this.serverMedia.length > 0;

    if (!filesToUpload.length && !hasExistingMedia) {
      this.showError(mediaType === 1 ? 'Please upload at least one image.' : 'Please upload at least one video.');
      return;
    }

    const shopIds = this.selectedShops.map((shop: any) => shop.id);
    const title = this.adForm.value.adTitle;
    const periodFrom = this.formatDate(this.adForm.get('startDate')?.value);
    const periodTo = this.formatDate(this.adForm.get('endDate')?.value);
    const duration = mediaType === 1 ? this.adForm.value.imageDuration : null;
    const muteVideo = mediaType === 2 ? (this.adForm.value.muteVideo ? 1 : 0) : 0;

    this.isUploading = true;
    this.uploadProgress = 0;

    this.uploadAndCreateAd(filesToUpload, {
      shopIds,
      title,
      periodFrom,
      periodTo,
      mediaType,
      duration,
      muteVideo
    });
  }

  private uploadAndCreateAd(
    files: File[],
    meta: {
      shopIds: any[];
      title: string;
      periodFrom: string | null;
      periodTo: string | null;
      mediaType: number;
      duration: number | null;
      muteVideo: number;
    }
  ) {
    let index = 0;
    const mediaNames: string[] = [];

    const next = () => {
      if (index >= files.length) {
        this.callCreateAd(mediaNames, meta);
        return;
      }

      const file = files[index];
      this.currentUploadingFile = file.name;
      const fileId = `file_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      this.adsSettingService.uploadFileChunked(file, fileId, {}, (progress: number) => {
        // We calculate progress based on all files uploading taking up to 80% out of the total 100%
        const baseProgress = (index / files.length) * 80;
        const fileProgress = (progress / 100) * (80 / files.length);
        this.uploadProgress = Math.round(baseProgress + fileProgress);
      }).subscribe({
        next: (response: any) => {
          const fileName = response?.fileName;
          if (!fileName) {
            this.isUploading = false;
            this.uploadProgress = 0;
            this.showError(`Upload response missing fileName for ${file.name}`);
            return;
          }

          mediaNames.push(fileName);
          index++;
          this.uploadProgress = Math.round((index / files.length) * 80); // upload stage
          next();
        },
        error: (error: any) => {
          this.isUploading = false;
          this.uploadProgress = 0;
          this.showError(`Upload failed for ${file.name}. ${error?.error?.message || 'Please try again.'}`);
        }
      });
    };

    next();
  }

  private callCreateAd(
    mediaNames: string[],
    meta: {
      shopIds: any[];
      title: string;
      periodFrom: string | null;
      periodTo: string | null;
      mediaType: number;
      duration: number | null;
      muteVideo: number;
    }
  ) {
    const payload = {
      shopIds: meta.shopIds,
      title: meta.title,
      periodFrom: meta.periodFrom,
      periodTo: meta.periodTo,
      mediaType: meta.mediaType,
      duration: meta.duration,
      muteVideo: meta.muteVideo,
      mediaNames,
      existingMediaNames: this.serverMedia.map(m => m.name),
      is_active: 1,
      is_default: 0
    };

    this.uploadProgress = 90;

    const request$ = this.adId > 0
      ? this.adsSettingService.updateAd(this.adId, payload)
      : this.adsSettingService.setAds(payload);

    request$.subscribe({
      next: () => {
        this.isUploading = false;
        this.uploadProgress = 100;
        this.snackBarService.openSnackBar({
          title: 'Success',
          message: this.adId > 0 ? 'Ad updated successfully!' : 'Ad created successfully!',
          type: 'success'
        });
        // Redirect back to list page for both insert and update
        this.router.navigate(['/utility/ads-setting']);
      },
      error: (error: any) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.showError(error?.error?.message || 'Error processing ad. Please try again.');
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.adForm.value.mediaType === 'image') this.isImageDragOver = true;
    else this.isVideoDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isImageDragOver = false;
    this.isVideoDragOver = false;
  }

  onImageDrop(event: DragEvent) {
    event.preventDefault();
    this.isImageDragOver = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('image/')) : [];
    if (files.length) this.validateAndSetImages(files);
  }

  onVideoDrop(event: DragEvent) {
    event.preventDefault();
    this.isVideoDragOver = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files).filter(f => f.type.startsWith('video/')) : [];
    if (files.length) this.validateAndSetVideos(files);
  }

  onImageFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files).filter(f => f.type.startsWith('image/')) : [];
    if (files.length) this.validateAndSetImages(files);
  }

  onVideoFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files).filter(f => f.type.startsWith('video/')) : [];
    if (files.length) this.validateAndSetVideos(files);
  }

  private validateAndSetImages(files: File[]) {
    const maxSize = 50 * 1024 * 1024;
    if (files.some(f => f.size > maxSize)) {
      this.showError('Some image files are too large. Maximum size is 50MB per image.');
      return;
    }

    this.imageFiles = [...this.imageFiles, ...files];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e: any) => this.imagePreviews.push(e.target.result);
      reader.readAsDataURL(file);
    });
  }

  private validateAndSetVideos(files: File[]) {
    const maxSize = 500 * 1024 * 1024;
    if (files.some(f => f.size > maxSize)) {
      this.showError('Some video files are too large. Maximum size is 500MB per video.');
      return;
    }

    this.videoFiles = [...this.videoFiles, ...files];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e: any) => this.videoPreviews.push(e.target.result);
      reader.readAsDataURL(file);
    });
  }

  deleteLocalImage(index: number) {
    this.imageFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);
  }

  deleteLocalVideo(index: number) {
    this.videoFiles.splice(index, 1);
    this.videoPreviews.splice(index, 1);
  }

  deleteServerMedia(media: any) {
    const dialogData = new ConfirmDialogModel("Confirm Delete", "Are you sure you want to permanently delete this media file from the remote folder?");
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.adsSettingService.deleteMedia(this.adId, media.name).subscribe({
          next: () => {
            this.serverMedia = this.serverMedia.filter(m => m.name !== media.name);
            this.snackBarService.openSnackBar({
              title: 'Success',
              message: 'Media deleted correctly.',
              type: 'success'
            });
          },
          error: (err) => {
            this.showError('Could not delete media at this time.');
          }
        });
      }
    });
  }

  private resetForm() {
    this.adForm.reset({ mediaType: 'image', imageDuration: 10, muteVideo: true });
    this.imageFiles = [];
    this.imagePreviews = [];
    this.videoFiles = [];
    this.videoPreviews = [];
    this.currentUploadingFile = '';
  }

  private showError(message: string) {
    this.snackBarService.openSnackBar({
      title: 'Error',
      message,
      type: 'error'
    });
  }

  private formatDate(value: any): string | null {
    if (!value) return null;
    if (typeof value?.format === 'function') return value.format('YYYY-MM-DD');
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }

  isDirty(): boolean {
    return this.adForm.dirty || this.imageFiles.length > 0 || this.videoFiles.length > 0;
  }
}
